/**
 * Attachment Rendering & Upload Helpers
 *
 * Handles:
 *   1. Post-processing static HTML to resolve attachment: URLs (images, audio, files)
 *   2. File paste/drop handling for TipTap editors
 *   3. Markdown reference format: ![filename](attachment:DOC_ID/BLOB_ID) for images,
 *      [filename](attachment:DOC_ID/BLOB_ID) for other files
 *
 * Uses "attachment:" prefix (not "blob:") to avoid collision with the browser's
 * native blob: URL scheme (URL.createObjectURL).
 */

import { getAttachmentUrl, populateAttachmentCache, uploadAttachment, validateFile, attachmentsOnDevice, isNeedsConnection, whenConnectionReturns, loadAttachmentDek } from '../../../../engine/services/attachment-service.js';
import { isSyncAvailable } from '../../../../engine/storage/index.js';
import { t } from '../../../../shared/i18n/index.js';

/**
 * If the editor cursor is inside a table cell, return the cell's content width (px).
 * Returns null if not in a table.
 */
export function getTableCellWidth(editor) {
  try {
    const { $from } = editor.state.selection;
    for (let d = $from.depth; d > 0; d--) {
      const name = $from.node(d).type.name;
      if (name === 'tableCell' || name === 'tableHeader') {
        const cellDom = editor.view.domAtPos($from.before(d) + 1)?.node;
        const td = cellDom?.closest?.('td, th') || cellDom;
        if (td && td.clientWidth) {
          const cs = getComputedStyle(td);
          return Math.round(td.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight));
        }
      }
    }
  } catch {}
  return null;
}

/** Prefix for attachment references in markdown */
const ATTACHMENT_PREFIX = 'attachment:';

/** Regex for attachment: URL pattern (used in src/href attributes) */
const ATTACHMENT_URL_PATTERN = /^attachment:([a-f0-9-]+)\/([a-f0-9-]+)$/;

// Tracks objectUrl → attachment:DOC/BLOB for reverse mapping
const objectUrlToAttachRef = new Map();

const VALID_ALIGNS = new Set(['left', 'center', 'right']);
function safeAlign(v) { return VALID_ALIGNS.has(v) ? v : 'center'; }

/** Escape markdown link-syntax chars in filenames to prevent injection */
function escapeMarkdownLink(name) {
  return (name || '').replace(/[[\]()\\]/g, '\\$&');
}

/**
 * Replace attachment: references in markdown with decrypted object URLs.
 * Call before loading content into a TipTap editor so images render inline.
 *
 * @param {string} markdown - Markdown with attachment:DOC/BLOB URLs
 * @param {string} docId - Current document ID
 * @param {CryptoKey} dek - Document encryption key
 * @returns {Promise<string>} Markdown with object URLs replacing attachment: refs
 */
export async function resolveAttachmentUrls(markdown, docId, dek) {
  if (!markdown || !dek || !markdown.includes(ATTACHMENT_PREFIX)) return markdown;

  // Collect all attachment refs
  const refs = [];
  const regex = /attachment:([a-f0-9-]+)\/([a-f0-9-]+)/g;
  let m;
  while ((m = regex.exec(markdown)) !== null) {
    // Its name in the note, for an error: ![name](attachment:…) or [name](attachment:…)
    const name = markdown.slice(0, m.index).match(/\[([^\]]*)\]\($/)?.[1];
    refs.push({ full: m[0], docId: m[1], attachmentId: m[2], name });
  }
  if (refs.length === 0) return markdown;

  // Deduplicate and resolve
  const unique = [...new Map(refs.map(r => [r.full, r])).values()];
  const resolved = new Map();

  await Promise.all(unique.map(async (ref) => {
    try {
      // A reference to another map's file (a note copied from it) needs that
      // map's key: read from the vault (null here).
      const { objectUrl } = await getAttachmentUrl(ref.docId, ref.attachmentId, ref.docId === docId ? dek : null, { name: ref.name });
      resolved.set(ref.full, objectUrl);
      objectUrlToAttachRef.set(objectUrl, ref.full);
    } catch (err) {
      console.warn('[Attachment] Failed to resolve:', ref.full, err);
    }
  }));

  // Replace in markdown
  let result = markdown;
  for (const [attachRef, objUrl] of resolved) {
    result = result.split(attachRef).join(objUrl);
  }
  return result;
}

/**
 * Clear the object URL → attachment ref mapping. Call on doc switch.
 */
export function clearAttachmentUrlMap() {
  objectUrlToAttachRef.clear();
}

/**
 * Look up the attachment: ref for a resolved object URL.
 * Returns the "attachment:DOC_ID/BLOB_ID" string, or null if not found.
 */
export function getAttachmentRef(objectUrl) {
  return objectUrlToAttachRef.get(objectUrl) || null;
}

/**
 * Register an object URL → attachment: ref mapping.
 * Call after upload so the editor can display the object URL immediately
 * and unresolveAttachmentUrls() can convert it back when saving.
 */
export function registerAttachmentUrl(objectUrl, attachRef) {
  objectUrlToAttachRef.set(objectUrl, attachRef);
}

export function unresolveAttachmentUrls(markdown) {
  if (!markdown) return markdown;
  let result = markdown;
  for (const [objUrl, attachRef] of objectUrlToAttachRef) {
    result = result.split(objUrl).join(attachRef);
  }
  return result;
}

/**
 * Sanitize HTML before setting innerHTML to prevent the browser from
 * fetching attachment: or external URLs that would trigger CSP errors.
 *
 * - Moves attachment: src/href to data attributes (picked up by renderAttachmentsInElement)
 * - Converts external <img> to clickable <a> links (only http/https allowed)
 *
 * @param {string} html - Raw HTML from markdownToHtml()
 * @returns {string} Sanitized HTML safe for innerHTML assignment
 */
export function prepareStaticHtml(html) {
  if (!html) return html;

  // Move attachment: src to data attribute so browser won't try to fetch it
  html = html.replace(
    /(<img\b[^>]*?\s)src=(["'])(attachment:[^"']+)\2/gi,
    '$1data-attachment-src=$2$3$2',
  );
  // Move attachment: href to data attribute
  html = html.replace(
    /(<a\b[^>]*?\s)href=(["'])(attachment:[^"']+)\2/gi,
    '$1data-attachment-href=$2$3$2',
  );

  // Convert external <img src="http(s)://..."> to clickable links.
  // CSP blocks external images anyway; showing a link is more useful.
  html = html.replace(
    /<img\b([^>]*?)\ssrc=(["'])(https?:\/\/[^"']+)\2([^>]*?)\s*\/?>/gi,
    (_match, before, _q, url, after) => {
      // Extract alt text if present for the link label
      const altMatch = (before + after).match(/alt=(["'])([^"']*)\1/i);
      const alt = altMatch?.[2] || '';
      const label = alt || url;
      // Escape HTML entities in URL for safe href attribute
      const safeUrl = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      const safeLabel = (label || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="external-image-link" title="${t('views.md.externalImage')}">\u{1F5BC} ${safeLabel}</a>`;
    },
  );

  return html;
}

/**
 * Post-process static HTML to resolve attachment: references.
 * Finds <img> / <a> with data-attachment-src / data-attachment-href
 * (set by prepareStaticHtml) and replaces them with decrypted content.
 *
 * @param {HTMLElement} el - Container element
 * @param {string} docId - Document ID (fallback if not in URL)
 * @param {CryptoKey} dek - Document encryption key; when not loaded yet (a map
 *   on this device drawn before its key was read, e.g. just moved here), it
 *   is loaded, then the references are resolved: never left blank.
 */
export function renderAttachmentsInElement(el, docId, dek) {
  if (!el) return;
  if (!dek) {
    if (docId && el.querySelector('img[data-attachment-src], a[data-attachment-href]')) {
      void loadAttachmentDek(docId)
        .then((key) => { if (key && el.isConnected) renderAttachmentsInElement(el, docId, key); })
        .catch(() => {});
    }
    return;
  }

  // Process images: <img data-attachment-src="attachment:DOC_ID/BLOB_ID">
  const images = el.querySelectorAll('img[data-attachment-src]');
  for (const img of images) {
    const rawSrc = img.getAttribute('data-attachment-src') || '';
    const match = rawSrc.match(ATTACHMENT_URL_PATTERN);
    if (!match) continue;
    const [, attachDocId, attachmentId] = match;
    const width = img.getAttribute('width');
    const align = img.getAttribute('data-align');
    const rotate = img.getAttribute('data-rotate');
    img.removeAttribute('data-attachment-src');
    const name = img.alt;
    img.alt = img.alt || 'Loading...';
    // A reference to another map's file (a note copied from it) needs that
    // map's key: read from the vault (null here).
    const fileDocId = attachDocId || docId;
    loadImageAttachment(img, fileDocId, attachmentId, fileDocId === docId ? dek : null, width, align, rotate, name);
  }

  // Process links: <a data-attachment-href="attachment:DOC_ID/BLOB_ID">
  const links = el.querySelectorAll('a[data-attachment-href]');
  for (const link of links) {
    const href = link.getAttribute('data-attachment-href') || '';
    const match = href.match(ATTACHMENT_URL_PATTERN);
    if (!match) continue;
    const [, attachDocId, attachmentId] = match;
    const filename = link.textContent || t('views.md.attachment');
    link.removeAttribute('data-attachment-href');
    const fileDocId = attachDocId || docId;
    loadLinkAttachment(link, fileDocId, attachmentId, filename, fileDocId === docId ? dek : null);
  }
}

/**
 * Replace an <img> with a decrypted image (or audio player / download link based on MIME).
 * `dek`: the key of the file's map, or null to read it from the vault.
 */
async function loadImageAttachment(img, docId, attachmentId, dek, width = null, align = null, rotate = null, name = img.alt) {
  try {
    const { objectUrl, mimeType, filename } = await getAttachmentUrl(docId, attachmentId, dek, { name });

    if (mimeType?.startsWith('audio/')) {
      // Audio: replace img with audio player
      const wrapper = document.createElement('div');
      wrapper.className = 'attachment-node attachment-loaded';
      const label = document.createElement('div');
      label.className = 'attachment-audio-label';
      label.textContent = filename || t('views.md.audio');
      const audio = document.createElement('audio');
      audio.src = objectUrl;
      audio.controls = true;
      audio.className = 'attachment-audio';
      wrapper.appendChild(label);
      wrapper.appendChild(audio);
      img.replaceWith(wrapper);
    } else {
      // Image: wrap in container for width/align, then set src
      const wrapper = document.createElement('div');
      wrapper.className = `attachment-static-wrapper attachment-align-${safeAlign(align)}`;
      const container = document.createElement('div');
      container.className = 'attachment-static-container';
      if (width) container.style.width = width + 'px';

      img.src = objectUrl;
      img.className = 'attachment-image';
      img.loading = 'lazy';
      const rotateInt = parseInt(rotate, 10) || 0;
      if (rotateInt) {
        container.style.transform = `rotate(${rotateInt}deg)`;
      }

      // Replace the bare <img> with the wrapper structure
      img.parentNode?.insertBefore(wrapper, img);
      container.appendChild(img);
      wrapper.appendChild(container);

      // Render caption from title attribute
      const caption = img.getAttribute('title');
      if (caption) {
        const captionDiv = document.createElement('div');
        captionDiv.className = 'image-caption-static';
        captionDiv.textContent = caption;
        wrapper.appendChild(captionDiv);
        img.removeAttribute('title'); // don't show as tooltip too
        // Reserve space for the absolutely-positioned caption
        requestAnimationFrame(() => {
          wrapper.style.paddingBottom = captionDiv.offsetHeight + 'px';
        });
      }
    }
  } catch (err) {
    // Replace invisible broken img with a visible placeholder
    const placeholder = document.createElement('div');
    placeholder.className = 'attachment-broken';
    if (isNeedsConnection(err)) {
      placeholder.classList.add('needs-connection');
      placeholder.textContent = t('vault.device.needsConnection');
      placeholder.title = t('vault.device.needsConnectionHint');
      retryWhenOnline(placeholder, () => {
        placeholder.replaceWith(img);
        loadImageAttachment(img, docId, attachmentId, dek, width, align, rotate, name);
      });
    } else {
      console.error('[Attachment] Failed to load image:', `${docId}/${attachmentId}`, err);
      placeholder.title = t('views.md.failedToLoad', { name: name || t('views.md.image') });
    }
    img.replaceWith(placeholder);
  }
}

/** Load again once the connection returns, if `el` is still shown then. */
function retryWhenOnline(el, retry) {
  whenConnectionReturns(() => { if (el.isConnected) retry(); });
}

/**
 * Replace an <a> with attachment: href with a proper download link.
 * `dek`: the key of the file's map, or null to read it from the vault.
 */
async function loadLinkAttachment(link, docId, attachmentId, filename, dek) {
  try {
    const { objectUrl, mimeType, filename: serverFilename } = await getAttachmentUrl(docId, attachmentId, dek, { name: filename });
    const displayName = serverFilename || filename;

    if (mimeType?.startsWith('image/')) {
      // Linked as [name](attachment:...) but it's an image — render inline
      const img = document.createElement('img');
      img.src = objectUrl;
      img.alt = displayName;
      img.className = 'attachment-image';
      img.loading = 'lazy';
      link.replaceWith(img);
    } else if (mimeType?.startsWith('audio/')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'attachment-node attachment-loaded';
      const label = document.createElement('div');
      label.className = 'attachment-audio-label';
      label.textContent = displayName;
      const audio = document.createElement('audio');
      audio.src = objectUrl;
      audio.controls = true;
      audio.className = 'attachment-audio';
      wrapper.appendChild(label);
      wrapper.appendChild(audio);
      link.replaceWith(wrapper);
    } else {
      // Document: update href for download
      link.href = objectUrl;
      link.download = displayName;
      link.target = '_blank';
      link.rel = 'noopener';
      link.className = 'attachment-file-link';
      // Add icon prefix
      const icon = document.createElement('span');
      icon.className = 'attachment-file-icon';
      icon.textContent = getFileIcon(mimeType);
      link.insertBefore(icon, link.firstChild);
    }
  } catch (err) {
    link.removeAttribute('href');
    if (isNeedsConnection(err)) {
      const originalClass = link.className;
      link.className = 'attachment-error needs-connection';
      link.textContent = `${filename} (${t('vault.device.needsConnection')})`;
      link.title = t('vault.device.needsConnectionHint');
      retryWhenOnline(link, () => {
        link.className = originalClass;
        link.textContent = filename;
        link.removeAttribute('title');
        loadLinkAttachment(link, docId, attachmentId, filename, dek);
      });
      return;
    }
    console.error('[Attachment] Failed to load link:', `${docId}/${attachmentId}`, err);
    link.textContent = t('views.md.failedToLoad', { name: filename });
    link.className = 'attachment-error';
  }
}

/**
 * Create a paste/drop handler for TipTap editors that uploads files
 * and inserts attachment: markdown references.
 *
 * @param {Object} options
 * @param {Function} options.getDocId - () => string
 * @param {Function} options.getDek - () => CryptoKey
 * @param {Function} options.getEditor - () => Editor (TipTap editor instance)
 * @param {Function} [options.onError] - (message) => void
 * @returns {{ handlePaste: Function, handleDrop: Function }}
 */
export function createFileHandlers({ getDocId, getDek, getEditor, onError }) {
  async function handleFiles(files) {
    const docId = getDocId?.();
    const dek = getDek?.();
    const editor = getEditor?.();
    if (!docId || !dek || !editor) {
      onError?.(t('views.md.uploadNotReady'));
      return;
    }

    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        onError?.(validation.error);
        continue;
      }

      try {
        const { attachmentId, filename, mimeType } = await uploadAttachment(docId, file, dek);
        const attachRef = `attachment:${docId}/${attachmentId}`;
        const safeName = escapeMarkdownLink(filename);

        // Persist `attachment:` refs in the editor state for both images
        // and other files — in collab mode the editor state IS the CRDT, so
        // an ephemeral blob: URL here would propagate to peers and die on
        // reload. Pre-seed the cache from the original file so the NodeView /
        // link click handler can render or open it without a server round-trip.
        populateAttachmentCache(docId, attachmentId, { blob: file, mimeType, filename });
        if (mimeType.startsWith('image/')) {
          const imgAttrs = { src: attachRef, alt: filename };
          const cellWidth = getTableCellWidth(editor);
          if (cellWidth) imgAttrs.width = cellWidth;
          editor.chain().focus().setImage(imgAttrs).run();
        } else {
          const md = `[${safeName}](${attachRef})`;
          editor.chain().focus().insertContent(md).run();
        }
      } catch (err) {
        console.error('[Attachment] Upload failed:', err);
        onError?.(err.message || t('views.md.uploadFailed'));
      }
    }
  }

  return {
    handlePaste: (view, event) => {
      const files = Array.from(event.clipboardData?.files || []);
      if (files.length === 0) return false;
      event.preventDefault();
      handleFiles(files);
      return true;
    },

    handleDrop: (view, event, slice, moved) => {
      if (moved) return false;
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length === 0) return false;
      event.preventDefault();
      handleFiles(files);
      return true;
    },
  };
}

/**
 * Create image + file attach buttons for an editor context.
 * Returns a container div with both buttons.
 *
 * @param {Object} options
 * @param {Function} options.getDocId - () => string
 * @param {Function} options.getDek - () => CryptoKey
 * @param {Function} options.getEditor - () => Editor
 * @param {Function} [options.onError] - (msg) => void
 * @returns {HTMLElement}
 */
export function createAttachButtons({ getDocId, getDek, getEditor, onError }) {
  const container = document.createElement('div');
  container.className = 'attach-buttons';

  // An online map's attachments go to the server; a map on this device keeps
  // them on the device. Without a server (the desktop app in local-only mode)
  // only maps on this device can take attachments: return the empty container
  // otherwise, so callers can keep appending it unconditionally.
  if (!isSyncAvailable() && !attachmentsOnDevice(getDocId?.())) return container;

  const imgBtn = makeButton(
    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>`,
    t('views.md.insertImage'),
    '.png,.jpg,.jpeg,.gif,.webp,.svg',
    { getDocId, getDek, getEditor, onError },
  );

  const fileBtn = makeButton(
    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
    </svg>`,
    t('views.md.attachFile'),
    '.pdf,.md,.txt,.csv,.json,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp3,.wav,.ogg,.m4a,.webm,.flac',
    { getDocId, getDek, getEditor, onError },
  );

  container.appendChild(imgBtn);
  container.appendChild(fileBtn);
  return container;
}

function makeButton(icon, title, accept, { getDocId, getDek, getEditor, onError }) {
  const btn = document.createElement('button');
  btn.className = 'attach-btn';
  btn.innerHTML = icon;
  btn.title = title;
  btn.type = 'button';

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.multiple = true;
  input.style.display = 'none';
  btn.appendChild(input);

  btn.addEventListener('click', (e) => {
    if (e.target === input) return;
    e.preventDefault();
    if (!getEditor?.()) {
      onError?.(t('views.md.clickToEditFirst'));
      return;
    }
    input.click();
  });

  input.addEventListener('change', async () => {
    const files = Array.from(input.files || []);
    input.value = '';
    if (files.length === 0) return;

    const docId = getDocId?.();
    const dek = getDek?.();
    const editor = getEditor?.();
    if (!docId || !dek || !editor) {
      onError?.(t('views.md.uploadNotReady'));
      return;
    }

    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        onError?.(validation.error);
        continue;
      }
      try {
        const { attachmentId, filename, mimeType } = await uploadAttachment(docId, file, dek);
        const attachRef = `attachment:${docId}/${attachmentId}`;
        // Persist the attachment ref directly (collab-safe). Pre-seed cache
        // from the original file so display / click open is instant.
        populateAttachmentCache(docId, attachmentId, { blob: file, mimeType, filename });
        if (mimeType.startsWith('image/')) {
          const imgAttrs = { src: attachRef, alt: filename };
          const cellWidth = getTableCellWidth(editor);
          if (cellWidth) imgAttrs.width = cellWidth;
          editor.chain().focus().setImage(imgAttrs).run();
        } else {
          const safeName = escapeMarkdownLink(filename);
          const md = `[${safeName}](${attachRef})`;
          editor.chain().focus().insertContent(md).run();
        }
      } catch (err) {
        console.error('[Attach] Upload failed:', err);
        onError?.(err.message || t('views.md.uploadFailed'));
      }
    }
  });

  return btn;
}

/**
 * Get an icon character for a file type.
 */
function getFileIcon(mimeType) {
  if (!mimeType) return '\u{1F4CE}';
  if (mimeType === 'application/pdf') return '\u{1F4C4}';
  if (mimeType.startsWith('text/')) return '\u{1F4DD}';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '\u{1F4CA}';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '\u{1F4CA}';
  if (mimeType.includes('word') || mimeType.includes('document')) return '\u{1F4C3}';
  return '\u{1F4CE}';
}
